import type { GameRenderer } from '@vpgc/render-pixi';
import { Assets, Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import type { BuildingType, InventoryItem, ViewFxEvent, ViewModel, ViewTile } from '../contract.js';
import { RESOURCE_LABELS, TILE_SIZE, type ResourceId } from '../domain/content.js';
import {
  computeActionButtonRect,
  computeBuildButtonRects,
  computeBuildMenuRects,
  computeBuildingPanelRects,
  computeDebugPanelRects,
  computeInventoryButtonRect,
  computeInventoryPanelRects,
  computeScreenLayout,
  computeTechTreeRects,
  computeToolsPanelRects,
  computeZonePanelRect,
  type Rect,
  type ScreenLayout,
} from './layout.js';

const FONT = 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const GAME_VERSION = '0.1.13';

interface TextureSet {
  recycler: Texture;
  storage: Texture;
  player: Record<ViewModel['player']['facing'], Texture[]>;
}

export class TemptrashRenderer implements GameRenderer<ViewModel> {
  private root: Container | null = null;
  private readonly world = new Graphics();
  private readonly spriteLayer = new Container();
  private readonly actors = new Graphics();
  private readonly ui = new Graphics();
  private readonly textLayer = new Container();
  private readonly hudText = new Text({ text: '', style: { fontFamily: FONT, fontSize: 14, fill: 0xf6edd9 } });
  private readonly statusText = new Text({ text: '', style: { fontFamily: FONT, fontSize: 12, fill: 0xa9bbb3 } });
  private readonly buildingSprites: Sprite[] = [];
  private readonly ghostSprite = new Sprite(Texture.EMPTY);
  private readonly playerSprite = new Sprite(Texture.EMPTY);
  private readonly seenFx = new Set<number>();
  private readonly particles: Array<{ kind: ViewFxEvent['kind']; resource: ResourceId; fromX: number; fromY: number; toX: number; toY: number; age: number; duration: number }> = [];
  private previousPlayerX: number | null = null;
  private previousPlayerY: number | null = null;
  private previousZoneId: ViewModel['currentZone']['id'] | null = null;
  private previousZonePct: number | null = null;
  private zonePulseFrames = 0;
  private textures: TextureSet = {
    recycler: Texture.EMPTY,
    storage: Texture.EMPTY,
    player: {
      north: [Texture.EMPTY],
      east: [Texture.EMPTY],
      south: [Texture.EMPTY],
      west: [Texture.EMPTY],
    },
  };

  async mount(stage: unknown): Promise<void> {
    this.root = stage as Container;
    this.ghostSprite.anchor.set(0.5);
    this.playerSprite.anchor.set(0.5, 0.72);
    this.root.addChild(this.world, this.spriteLayer, this.actors, this.ui, this.hudText, this.statusText, this.textLayer);
    this.spriteLayer.addChild(this.ghostSprite, this.playerSprite);
    this.textures = await loadPixelArtTextures();
  }

  draw(view: ViewModel): void {
    const width = Math.max(1, globalThis.innerWidth || 960);
    const height = Math.max(1, globalThis.innerHeight || 540);
    const layout = computeScreenLayout(width, height);

    this.drawWorld(view, layout, width, height);
    this.drawActionHighlight(view, layout);
    this.drawBuildings(view, layout);
    this.drawGhost(view, layout);
    this.captureFx(view);
    this.drawActors(view, layout);
    this.drawUi(view, layout, width, height);
  }

  destroy(): void {
    this.root?.removeChildren();
    this.root = null;
  }

  private drawWorld(view: ViewModel, layout: ScreenLayout, width: number, height: number): void {
    this.world.clear();
    this.world.rect(0, 0, width, height).fill({ color: 0x0b1210 });
    this.world.rect(layout.x, layout.y, layout.w, layout.h).fill({ color: 0x17211c });

    for (const tile of view.tiles) {
      const sx = worldToScreenX(tile.x * TILE_SIZE, view, layout);
      const sy = worldToScreenY(tile.y * TILE_SIZE, view, layout);
      const size = TILE_SIZE * layout.scale;
      if (!tile.revealed) {
        this.world.rect(sx, sy, size + 0.5, size + 0.5).fill({ color: 0x060c0a });
        continue;
      }

      this.world.rect(sx, sy, size + 0.5, size + 0.5).fill({ color: groundColor(tile) });
      this.world.rect(sx, sy, size, size).stroke({ color: 0x0f1814, width: 1, alpha: 0.34 });
      if (tile.restored) this.drawRestoredGround(tile, sx, sy, size);
      if (tile.goo) this.drawGoo(tile, sx, sy, size);
      if (tile.wall) this.drawRuinsWall(tile, sx, sy, size);
      else if (tile.amount > 0) this.drawGarbagePile(tile, sx, sy, size);
      if (!tile.visible) this.world.rect(sx, sy, size + 0.5, size + 0.5).fill({ color: 0x060c0a, alpha: 0.6 });
    }
  }

  private drawBuildings(view: ViewModel, layout: ScreenLayout): void {
    while (this.buildingSprites.length < view.buildings.length) {
      const sprite = new Sprite(Texture.EMPTY);
      sprite.anchor.set(0.5);
      this.buildingSprites.push(sprite);
      this.spriteLayer.addChildAt(sprite, Math.max(0, this.spriteLayer.children.length - 1));
    }

    for (let i = 0; i < this.buildingSprites.length; i++) {
      const sprite = this.buildingSprites[i];
      const building = view.buildings[i];
      if (building === undefined) {
        sprite.visible = false;
        continue;
      }

      const texture = this.textureForBuilding(building.type);
      if (texture === Texture.EMPTY) {
        sprite.visible = false;
        this.drawBuildingFallback(
          building.type,
          worldToScreenX(building.tileX * TILE_SIZE, view, layout),
          worldToScreenY(building.tileY * TILE_SIZE, view, layout),
          TILE_SIZE * layout.scale,
          building.width,
          building.height,
          building.status === 'Processing',
        );
        continue;
      }

      sprite.visible = true;
      sprite.texture = texture;
      sprite.position.set(worldToScreenX(building.x, view, layout), worldToScreenY(building.y, view, layout));
      sprite.width = TILE_SIZE * building.width * layout.scale * 1.05;
      sprite.height = TILE_SIZE * building.height * layout.scale * 1.05;
      sprite.alpha = building.status === 'Processing' ? 1 : 0.94;
      sprite.tint = 0xffffff;
    }
  }

  private drawGhost(view: ViewModel, layout: ScreenLayout): void {
    const type = view.buildMenu.pendingBuildType;
    if (type === null) {
      this.ghostSprite.visible = false;
      return;
    }

    const width = type === 'storage' ? 2 : 1;
    const height = type === 'storage' ? 2 : 1;
    const texture = this.textureForBuilding(type);
    const color = view.actionTarget.kind === 'place' ? 0x9ccf84 : 0xd55c43;
    if (texture === Texture.EMPTY) {
      this.ghostSprite.visible = false;
      this.drawBuildingFallback(
        type,
        worldToScreenX(view.actionTarget.tileX * TILE_SIZE, view, layout),
        worldToScreenY(view.actionTarget.tileY * TILE_SIZE, view, layout),
        TILE_SIZE * layout.scale,
        width,
        height,
        false,
        true,
        color,
      );
      return;
    }

    this.ghostSprite.visible = true;
    this.ghostSprite.texture = texture;
    this.ghostSprite.position.set(
      worldToScreenX((view.actionTarget.tileX + width * 0.5) * TILE_SIZE, view, layout),
      worldToScreenY((view.actionTarget.tileY + height * 0.5) * TILE_SIZE, view, layout),
    );
    this.ghostSprite.width = TILE_SIZE * width * layout.scale * 1.05;
    this.ghostSprite.height = TILE_SIZE * height * layout.scale * 1.05;
    this.ghostSprite.alpha = 0.48;
    this.ghostSprite.tint = color;
  }

  private drawActionHighlight(view: ViewModel, layout: ScreenLayout): void {
    const size = TILE_SIZE * layout.scale;
    const x = worldToScreenX(view.actionTarget.tileX * TILE_SIZE, view, layout);
    const y = worldToScreenY(view.actionTarget.tileY * TILE_SIZE, view, layout);
    const color = actionColor(view.actionTarget.kind);
    this.world
      .rect(x + 2, y + 2, size - 4, size - 4)
      .stroke({ color, width: 2.5, alpha: 0.95 })
      .rect(x + size * 0.18, y + size * 0.18, size * 0.64, size * 0.64)
      .fill({ color, alpha: 0.12 });
    if (view.gather !== null && view.gather.active) {
      const gx = worldToScreenX(view.gather.tileX * TILE_SIZE, view, layout);
      const gy = worldToScreenY(view.gather.tileY * TILE_SIZE, view, layout);
      this.world.roundRect(gx + 4, gy + size - 8, size - 8, 4, 2).fill({ color: 0x18231f, alpha: 0.94 });
      this.world.roundRect(gx + 4, gy + size - 8, (size - 8) * view.gather.progress, 4, 2).fill({ color: 0xf0c866, alpha: 0.95 });
    }
  }

  private drawGarbagePile(tile: ViewTile, sx: number, sy: number, size: number): void {
    const padding = size * (0.24 - Math.min(tile.amount, 5) * 0.022);
    const x = sx + padding;
    const y = sy + padding + size * 0.06;
    const w = size - padding * 2;
    const h = size - padding * 1.55;
    this.world
      .roundRect(x, y + h * 0.12, w, h * 0.78, 3)
      .fill({ color: pileColor(tile.amount) })
      .stroke({ color: tile.blocked ? 0x91513f : 0x313a36, width: 1, alpha: 0.75 })
      .rect(x + w * 0.14, y + h * 0.08, w * 0.34, h * 0.22)
      .fill({ color: tile.amount >= 4 ? 0xb97d4f : 0x91a7ae })
      .rect(x + w * 0.58, y + h * 0.22, w * 0.22, h * 0.16)
      .fill({ color: tile.variant % 2 === 0 ? 0x91a7ae : 0xb97d4f });
    if (tile.gooSource) {
      this.world.circle(x + w * 0.78, y + h * 0.72, Math.max(2, size * 0.08)).fill({ color: 0xa8c94f, alpha: 0.96 });
    }
  }

  private drawGoo(tile: ViewTile, sx: number, sy: number, size: number): void {
    const wobble = (tile.variant % 3) * size * 0.025;
    this.world
      .ellipse(sx + size * 0.5, sy + size * 0.6, size * 0.42, size * (0.23 + wobble / size))
      .fill({ color: 0x697c35, alpha: 0.9 })
      .circle(sx + size * 0.27, sy + size * 0.47, size * 0.12)
      .fill({ color: 0x839848, alpha: 0.88 })
      .circle(sx + size * 0.68, sy + size * 0.54, size * 0.08)
      .fill({ color: 0x9aae55, alpha: 0.82 });
  }

  private drawRestoredGround(tile: ViewTile, sx: number, sy: number, size: number): void {
    const flower = tile.variant % 3 === 0;
    this.world
      .moveTo(sx + size * 0.24, sy + size * 0.78)
      .lineTo(sx + size * 0.2, sy + size * 0.58)
      .moveTo(sx + size * 0.24, sy + size * 0.78)
      .lineTo(sx + size * 0.32, sy + size * 0.62)
      .stroke({ color: 0x7fa75f, width: Math.max(1, size * 0.045), alpha: 0.9 });
    if (flower) this.world.circle(sx + size * 0.72, sy + size * 0.36, Math.max(1.5, size * 0.055)).fill({ color: 0xe6d487, alpha: 0.94 });
  }

  private drawRuinsWall(tile: ViewTile, sx: number, sy: number, size: number): void {
    const base = shade(tile.zoneColor, 0.48);
    this.world
      .rect(sx + size * 0.08, sy + size * 0.12, size * 0.84, size * 0.76)
      .fill({ color: base, alpha: 0.98 })
      .stroke({ color: 0xb8b19a, width: 1.5, alpha: 0.72 })
      .rect(sx + size * 0.16, sy + size * 0.18, size * 0.26, size * 0.2)
      .fill({ color: shade(base, 1.2), alpha: 0.94 })
      .rect(sx + size * 0.48, sy + size * 0.18, size * 0.34, size * 0.2)
      .fill({ color: shade(base, 0.86), alpha: 0.94 })
      .rect(sx + size * 0.2, sy + size * 0.46, size * 0.38, size * 0.2)
      .fill({ color: shade(base, 0.78), alpha: 0.94 })
      .rect(sx + size * 0.64, sy + size * 0.48, size * 0.18, size * 0.18)
      .fill({ color: shade(base, 1.1), alpha: 0.94 });
  }

  private drawBuildingFallback(
    type: BuildingType,
    x: number,
    y: number,
    tileSize: number,
    width: number,
    height: number,
    active: boolean,
    ghost = false,
    ghostColor = 0x9ccf84,
  ): void {
    const w = tileSize * width;
    const h = tileSize * height;
    const alpha = ghost ? 0.42 : 0.95;
    const stroke = ghost ? ghostColor : active ? 0xf0c866 : type === 'storage' ? 0xd6b963 : type === 'drill' ? 0xd55c43 : type === 'gatherer' ? 0x9ccf84 : 0x7aa590;
    const fill = type === 'storage' ? 0x3f3427 : type === 'drill' ? 0x3d2a28 : type === 'carrier_drone' ? 0x22364a : type === 'gatherer' ? 0x263b2c : 0x224844;
    this.world
      .roundRect(x + tileSize * 0.08, y + tileSize * 0.08, w - tileSize * 0.16, h - tileSize * 0.16, 5)
      .fill({ color: fill, alpha })
      .stroke({ color: stroke, width: 2, alpha: 0.92 });
    if (type === 'storage') {
      this.world
        .rect(x + w * 0.18, y + h * 0.24, w * 0.64, h * 0.18)
        .fill({ color: 0xd6b963, alpha })
        .rect(x + w * 0.2, y + h * 0.54, w * 0.24, h * 0.18)
        .fill({ color: 0x8a6957, alpha })
        .rect(x + w * 0.56, y + h * 0.54, w * 0.24, h * 0.18)
        .fill({ color: 0x9ccf84, alpha });
      return;
    }
    if (type === 'gatherer') {
      this.world.circle(x + w * 0.5, y + h * 0.48, Math.min(w, h) * 0.22).fill({ color: 0x9ccf84, alpha });
      this.world.rect(x + w * 0.28, y + h * 0.62, w * 0.44, h * 0.12).fill({ color: 0xe5d8b5, alpha });
      return;
    }
    if (type === 'carrier_drone') {
      this.world.circle(x + w * 0.5, y + h * 0.5, Math.min(w, h) * 0.2).fill({ color: 0x75aadb, alpha });
      this.world.rect(x + w * 0.2, y + h * 0.46, w * 0.6, h * 0.08).fill({ color: 0xe5d8b5, alpha });
      return;
    }
    if (type === 'drill') {
      this.world.moveTo(x + w * 0.5, y + h * 0.18).lineTo(x + w * 0.76, y + h * 0.74).lineTo(x + w * 0.24, y + h * 0.74).closePath().fill({ color: 0xd55c43, alpha });
      return;
    }
    this.world
      .rect(x + w * 0.25, y + h * 0.25, w * 0.5, h * 0.18)
      .fill({ color: 0xe5d8b5, alpha })
      .rect(x + w * 0.34, y + h * 0.52, w * 0.32, h * 0.18)
      .fill({ color: 0xd55c43, alpha });
  }

  private drawActors(view: ViewModel, layout: ScreenLayout): void {
    this.actors.clear();
    const size = TILE_SIZE * layout.scale;
    for (const unit of view.mobileUnits) {
      const ux = worldToScreenX(unit.x, view, layout);
      const uy = worldToScreenY(unit.y, view, layout);
      const r = Math.max(5, 8 * layout.scale);
      this.actors
        .circle(ux, uy, r)
        .fill({ color: 0x75aadb, alpha: 0.96 })
        .rect(ux - r * 1.3, uy - r * 0.18, r * 2.6, r * 0.36)
        .fill({ color: 0xe5d8b5, alpha: 0.86 })
        .rect(ux - r, uy - r * 1.55, r * 2, 3)
        .fill({ color: 0x17211c })
        .rect(ux - r, uy - r * 1.55, r * 2 * (unit.cargo / Math.max(1, unit.cargoCapacity)), 3)
        .fill({ color: 0xf0c866 });
    }
    const playerFrames = this.textures.player[view.player.facing];
    const moving =
      this.previousPlayerX !== null &&
      this.previousPlayerY !== null &&
      Math.hypot(view.player.x - this.previousPlayerX, view.player.y - this.previousPlayerY) > 0.1;
    const frame = moving && playerFrames.length > 1 ? Math.floor(view.tick / 8) % playerFrames.length : 0;
    const playerTexture = playerFrames[frame] ?? Texture.EMPTY;
    this.playerSprite.visible = playerTexture !== Texture.EMPTY;
    this.playerSprite.texture = playerTexture;
    this.playerSprite.position.set(worldToScreenX(view.player.x, view, layout), worldToScreenY(view.player.y, view, layout));
    this.playerSprite.width = size * 1.45;
    this.playerSprite.height = size * 1.45;

    const px = worldToScreenX(view.player.x, view, layout);
    const py = worldToScreenY(view.player.y, view, layout);
    const r = Math.max(8, 11 * layout.scale);
    if (!this.playerSprite.visible) {
      this.actors
        .circle(px, py + r * 0.18, r * 0.72)
        .fill({ color: 0x213f48 })
        .circle(px, py - r * 0.3, r * 0.66)
        .fill({ color: 0xe6bd59 })
        .rect(px - r * 0.38, py - r * 0.48, r * 0.76, r * 0.28)
        .fill({ color: 0xdfe9dd })
        .circle(px + facingX(view.player.facing) * r * 0.68, py + facingY(view.player.facing) * r * 0.68, r * 0.22)
        .fill({ color: 0xfff0b3 });
    }

    const carriedTrash = view.inventory.slots.reduce((total, slot) => total + (slot.item?.kind === 'trash' ? slot.item.quantity : 0), 0);
    if (carriedTrash > 0) {
      const ratio = Math.min(1, carriedTrash / (view.inventory.capacity * 10));
      this.actors
        .roundRect(px - r * 1.15, py + r * 0.2, r * 2.3, r * (0.22 + ratio * 0.42), 3)
        .fill({ color: ratio >= 1 ? 0xe3c15e : 0x7e735f, alpha: 0.95 })
        .stroke({ color: ratio >= 1 ? 0xffe08a : 0xa9bbb3, width: 1, alpha: 0.85 });
    }

    this.previousPlayerX = view.player.x;
    this.previousPlayerY = view.player.y;
    this.drawParticles(view, layout);
  }

  private drawUi(view: ViewModel, layout: ScreenLayout, width: number, height: number): void {
    this.ui.clear();
    for (const child of this.textLayer.removeChildren()) child.destroy();

    const topH = Math.max(76, 84 * layout.scale);
    this.ui.rect(0, 0, width, topH).fill({ color: 0x0d1713, alpha: 0.94 });
    this.ui.rect(0, topH - 3, width, 3).fill({ color: 0xd6b963, alpha: 0.35 });

    this.drawResourcePills(view, layout);
    this.drawBottomControls(view, layout);
    this.drawZonePanel(view, layout);
    this.drawInventoryButton(view, layout);

    this.hudText.position.set(18, layout.compact ? 34 : 40);
    this.hudText.style.fontSize = layout.compact ? 12 : 14;
    this.hudText.text = `Bag ${view.inventory.used}/${view.inventory.capacity}  Action ${view.actionTarget.label}  Hold Space to clean  B debug`;

    this.statusText.position.set(layout.compact ? 124 : 146, height - 19);
    this.statusText.text = `Tech ${view.techTree.points}  Cleared ${view.stats.tilesCleared}  v${GAME_VERSION}${view.statusMessage === null ? '' : `  ${view.statusMessage}`}`;

    if (view.streak.enabled) this.drawStreak(view, width, layout);
    this.drawBuildMenu(view, layout);
    this.drawInventoryPanel(view, layout);
    this.drawBuildingPanel(view, layout);
    this.drawDebugPanel(view, layout);
    this.drawTechTree(view, layout);
  }

  private drawBottomControls(view: ViewModel, layout: ScreenLayout): void {
    const action = computeActionButtonRect(layout);
    const buttons = computeBuildButtonRects(layout);
    this.ui
      .roundRect(action.x, action.y, action.w, action.h, 8)
      .fill({ color: 0x27413e, alpha: 0.96 })
      .stroke({ color: 0xf0c866, width: 2, alpha: 0.95 })
      .circle(action.x + action.w * 0.5, action.y + action.h * 0.5, action.w * 0.2)
      .stroke({ color: 0xf0c866, width: 4, alpha: 0.94 });
    this.drawPanelText('Hands', action.x + 10, action.y + 8, 13, 0xf6edd9);
    this.drawPanelText('Hold Space', action.x + 10, action.y + action.h - 20, 11, 0xa9bbb3);

    this.drawCommandButton(buttons.build, 'Build', 'V', 0x75aadb);
    this.drawCommandButton(buttons.tech, 'Tech', 'T', 0xd6b963, view.techTree.points > 0 ? `${view.techTree.points}` : null);
  }

  private drawCommandButton(rect: Rect, label: string, key: string, color: number, badge: string | null = null): void {
    this.ui
      .roundRect(rect.x, rect.y, rect.w, rect.h, 7)
      .fill({ color: 0x17241f, alpha: 0.97 })
      .stroke({ color, width: 1.5, alpha: 0.9 })
      .roundRect(rect.x + 8, rect.y + 8, rect.h - 16, rect.h - 16, 6)
      .fill({ color, alpha: 0.88 });
    this.drawPanelText(label, rect.x + 42, rect.y + 8, 13, 0xf6edd9);
    this.drawPanelText(key, rect.x + 42, rect.y + 25, 11, 0xa9bbb3);
    if (badge !== null) {
      this.ui.circle(rect.x + rect.w - 14, rect.y + 13, 9).fill({ color: 0xe3c15e, alpha: 0.95 });
      this.drawPanelText(badge, rect.x + rect.w - 17, rect.y + 7, 10, 0x101a16);
    }
  }

  private drawCycleButton(rect: Rect, label: string, key: string): void {
    this.ui
      .roundRect(rect.x, rect.y, rect.w, rect.h, 7)
      .fill({ color: 0x17241f, alpha: 0.97 })
      .stroke({ color: 0xf0c866, width: 1.4, alpha: 0.86 });
    this.drawPanelText(label, rect.x + rect.w * 0.38, rect.y + 6, 16, 0xf6edd9);
    this.drawPanelText(key, rect.x + rect.w * 0.34, rect.y + rect.h - 16, 9, 0xa9bbb3);
  }

  private drawInventoryButton(view: ViewModel, layout: ScreenLayout): void {
    const rect = computeInventoryButtonRect(layout);
    const full = view.inventory.used >= view.inventory.capacity;
    this.ui
      .roundRect(rect.x, rect.y, rect.w, rect.h, 7)
      .fill({ color: full ? 0x493f27 : 0x1a2a25, alpha: 0.97 })
      .stroke({ color: full ? 0xe3c15e : 0x7aa590, width: 1.5, alpha: 0.95 })
      .roundRect(rect.x + 13, rect.y + rect.h * 0.31, 22, 18, 3)
      .fill({ color: full ? 0xe3c15e : 0x9ccf84, alpha: 0.95 })
      .moveTo(rect.x + 18, rect.y + rect.h * 0.32)
      .quadraticCurveTo(rect.x + 24, rect.y + rect.h * 0.14, rect.x + 30, rect.y + rect.h * 0.32)
      .stroke({ color: full ? 0xe3c15e : 0x9ccf84, width: 2, alpha: 0.9 });
    this.drawPanelText('Bag', rect.x + 43, rect.y + 8, 13, 0xf6edd9);
    this.drawPanelText(`I  ${view.inventory.used}/${view.inventory.capacity}`, rect.x + 43, rect.y + 25, 11, full ? 0xe3c15e : 0xa9bbb3);
  }

  private drawZonePanel(view: ViewModel, layout: ScreenLayout): void {
    const rect = computeZonePanelRect(layout);
    const zonePct = Math.round(view.currentZone.percentCleared * 100);
    const zoneChanged = this.previousZoneId !== null && this.previousZoneId !== view.currentZone.id;
    const progressChanged = this.previousZonePct !== null && this.previousZonePct !== zonePct;
    if (zoneChanged || progressChanged) this.zonePulseFrames = zoneChanged ? 72 : 42;
    this.previousZoneId = view.currentZone.id;
    this.previousZonePct = zonePct;
    const pulse = this.zonePulseFrames > 0 ? Math.min(1, this.zonePulseFrames / 42) : 0;
    if (this.zonePulseFrames > 0) this.zonePulseFrames -= 1;
    const border = pulse > 0 ? 0xf0c866 : 0x7aa590;
    const fillW = Math.max(0, Math.min(1, view.currentZone.percentCleared)) * (rect.w - 24);

    this.ui
      .roundRect(rect.x, rect.y, rect.w, rect.h, 7)
      .fill({ color: pulse > 0 ? 0x22362d : 0x14231e, alpha: 0.97 })
      .stroke({ color: border, width: pulse > 0 ? 2.4 : 1.5, alpha: 0.9 })
      .roundRect(rect.x + 12, rect.y + rect.h - 22, rect.w - 24, 9, 4)
      .fill({ color: 0x25342e, alpha: 0.96 })
      .roundRect(rect.x + 12, rect.y + rect.h - 22, fillW, 9, 4)
      .fill({ color: view.currentZone.techAwarded ? 0xd6b963 : 0x9ccf84, alpha: 0.96 });

    this.drawPanelText('Zone', rect.x + 12, rect.y + 9, 10, 0xa9bbb3);
    this.drawPanelText(shortText(view.currentZone.name, layout.compact ? 20 : 26), rect.x + 12, rect.y + 25, 15, 0xf6edd9);
    this.drawPanelText(`${zonePct}%`, rect.x + rect.w - 48, rect.y + 24, 17, pulse > 0 ? 0xf0c866 : 0xd6b963);
    const awarded = view.currentZone.techAwarded ? 'Tech awarded' : 'Tech pending';
    this.drawPanelText(`${view.currentZone.clearedTrashTiles}/${view.currentZone.totalTrashTiles} clean tiles  ${awarded}`, rect.x + 12, rect.y + 47, 10, 0xa9bbb3);
  }

  private drawBuildMenu(view: ViewModel, layout: ScreenLayout): void {
    if (!view.buildMenu.open) return;
    const rects = computeBuildMenuRects(layout);
    this.drawModalFrame(rects.panel, layout.w, layout.h);
    this.drawPanelText('Build', rects.panel.x + 16, rects.panel.y + 14, layout.compact ? 19 : 22, 0xf6edd9);
    this.drawPanelText('Pick a building, then confirm on an adjacent clear tile.', rects.panel.x + 16, rects.panel.y + 43, 12, 0xa9bbb3);
    view.buildMenu.options.forEach((option, index) => {
      const rect = rects.options[index];
      if (rect === undefined) return;
      this.drawBuildOption(rect, option.name, option.description, option.footprint, formatCostBag(option.cost), String(index + 1), buildingAccent(option.type), option.canBuild, view.buildMenu.focusedIndex === index);
    });
    this.drawPanelButton(rects.close, 'Close', true);
  }

  private drawBuildOption(rect: Rect, name: string, description: string, footprint: string, cost: string, key: string, color: number, enabled: boolean, focused: boolean): void {
    this.ui
      .roundRect(rect.x, rect.y, rect.w, rect.h, 7)
      .fill({ color: enabled ? 0x1b2822 : 0x171f1b, alpha: 0.98 })
      .stroke({ color: focused ? 0xf0c866 : enabled ? color : 0x5d6962, width: focused ? 2.4 : 1.7, alpha: 0.9 })
      .circle(rect.x + 22, rect.y + 27, 12)
      .fill({ color: enabled ? color : 0x758177, alpha: 0.95 })
      .rect(rect.x + 42, rect.y + 19, 28, 18)
      .fill({ color: 0xf6edd9, alpha: enabled ? 0.75 : 0.32 });
    this.drawPanelText(name, rect.x + 14, rect.y + 50, 14, enabled ? 0xf6edd9 : 0x879188);
    this.drawPanelText(`${footprint}  ${shortText(description, 28)}`, rect.x + 14, rect.y + 72, 11, enabled ? 0xa9bbb3 : 0x758177);
    this.drawPanelText(`Cost ${cost}`, rect.x + 14, rect.y + rect.h - 22, 11, enabled ? 0xd6b963 : 0x879188);
    this.drawPanelText(key, rect.x + rect.w - 22, rect.y + 10, 12, enabled ? 0xd6b963 : 0x879188);
  }

  private drawInventoryPanel(view: ViewModel, layout: ScreenLayout): void {
    if (!view.inventoryPanel.open) return;
    const rects = computeInventoryPanelRects(layout, view.inventoryPanel.slots.length);
    this.drawModalFrame(rects.panel, layout.w, layout.h);
    this.drawPanelText('Inventory', rects.panel.x + 18, rects.panel.y + 14, layout.compact ? 19 : 22, 0xf6edd9);
    this.drawPanelText('Drag to rearrange. WASD moves focus.', rects.panel.x + 18, rects.panel.y + 42, 12, 0xa9bbb3);
    this.drawPanelButton(rects.close, 'Close', true);

    for (const slot of view.inventoryPanel.slots) {
      const rect = rects.slots[slot.index];
      if (rect === undefined) continue;
      this.drawSlot(rect, slot.item, slot.index === view.inventoryPanel.focusedSlot);
    }
  }

  private drawBuildingPanel(view: ViewModel, layout: ScreenLayout): void {
    const panel = view.buildingPanel;
    if (panel === null) return;
    const rects = computeBuildingPanelRects(layout, panel.playerInventory.length, panel.inbox.length, panel.outbox.length, panel.storage.length);
    this.drawModalFrame(rects.panel, layout.w, layout.h);
    this.drawPanelText(panel.name, rects.panel.x + 16, rects.panel.y + 14, layout.compact ? 18 : 21, 0xf6edd9);
    this.drawPanelText(`${panel.status}  Accepts ${panel.accepts}`, rects.panel.x + 16, rects.panel.y + 42, 12, 0xa9bbb3);

    const barY = rects.panel.y + 62;
    this.ui.roundRect(rects.panel.x + 16, barY, rects.panel.w - 32, 8, 4).fill({ color: 0x25342e, alpha: 0.96 });
    this.ui.roundRect(rects.panel.x + 16, barY, (rects.panel.w - 32) * panel.progress, 8, 4).fill({ color: 0x9ccf84, alpha: 0.96 });

    this.drawPanelText('Player', rects.playerSlots[0]?.x ?? rects.panel.x + 16, rects.panel.y + 76 - 18, 12, 0xf6edd9);
    for (const slot of panel.playerInventory) {
      const rect = rects.playerSlots[slot.index];
      if (rect !== undefined) this.drawSlot(rect, slot.item, false);
    }

    if (panel.type === 'storage') {
      this.drawPanelText('Storage', rects.storageSlots[0]?.x ?? rects.panel.x + rects.panel.w * 0.53, rects.panel.y + 76 - 18, 12, 0xf6edd9);
      panel.storage.forEach((item, index) => {
        const rect = rects.storageSlots[index];
        if (rect !== undefined) this.drawSlot(rect, item, false);
      });
    } else {
      this.drawPanelText('Input', rects.inboxSlots[0]?.x ?? rects.panel.x + rects.panel.w * 0.53, rects.panel.y + 76 - 18, 12, 0xf6edd9);
      panel.inbox.forEach((item, index) => {
        const rect = rects.inboxSlots[index];
        if (rect !== undefined) this.drawSlot(rect, item, false);
      });
      this.drawPanelText('Output', rects.outboxSlots[0]?.x ?? rects.panel.x + rects.panel.w * 0.53, (rects.outboxSlots[0]?.y ?? rects.panel.y + 130) - 18, 12, 0xf6edd9);
      panel.outbox.forEach((item, index) => {
        const rect = rects.outboxSlots[index];
        if (rect !== undefined) this.drawSlot(rect, item, false);
      });
    }

    this.drawPanelButton(rects.deposit, 'Deposit', panel.canDepositSelected, panel.focusedCommand === 'depositSelected');
    this.drawPanelButton(rects.depositAll, 'All In', true, panel.focusedCommand === 'depositAll');
    this.drawPanelButton(rects.collect, panel.type === 'storage' ? 'Take' : 'Collect', panel.type === 'storage' || panel.canCollectOutput, panel.focusedCommand === 'collectOutput');
    this.drawPanelButton(rects.remove, 'Remove', panel.canRemove, panel.focusedCommand === 'removeBuilding');
    this.drawPanelButton(rects.close, 'Close', true, panel.focusedCommand === 'close');
  }

  private drawTechTree(view: ViewModel, layout: ScreenLayout): void {
    if (!view.techTree.open) return;
    const rects = computeTechTreeRects(layout);
    this.drawModalFrame(rects.panel, layout.w, layout.h);
    this.drawPanelText('Tech Tree', rects.panel.x + 16, rects.panel.y + 14, layout.compact ? 19 : 22, 0xf6edd9);
    this.drawPanelText(`Points ${view.techTree.points}. Clear zones to earn more.`, rects.panel.x + 16, rects.panel.y + 43, 12, 0xa9bbb3);
    view.techTree.options.forEach((option, index) => {
      const rect = rects.options[index];
      if (rect === undefined) return;
      this.drawTechOption(rect, option.name, option.description, option.level, option.maxLevel, option.cost, String(index + 1), techAccent(option.category), option.canBuy, view.techTree.focusedIndex === index);
    });
    this.drawPanelButton(rects.close, 'Close', true);
  }

  private drawToolsPanel(view: ViewModel, layout: ScreenLayout): void {
    if (!view.toolsPanel.open) return;
    const rects = computeToolsPanelRects(layout);
    this.drawModalFrame(rects.panel, layout.w, layout.h);
    this.drawPanelText('Tools', rects.panel.x + 16, rects.panel.y + 14, layout.compact ? 19 : 22, 0xf6edd9);
    this.drawPanelText('Make usable tools. Tools take inventory slots.', rects.panel.x + 16, rects.panel.y + 43, 12, 0xa9bbb3);
    view.toolsPanel.recipes.forEach((recipe, index) => {
      const rect = rects.options[index];
      if (rect === undefined) return;
      this.drawToolRecipe(rect, recipe.name, recipe.description, recipe.status, recipe.owned, formatCostBag(recipe.cost), String(index + 1), itemColor(recipe.resultItemId), recipe.canCraft, view.toolsPanel.focusedIndex === index);
    });
    this.drawPanelButton(rects.close, 'Close', true);
  }

  private drawDebugPanel(view: ViewModel, layout: ScreenLayout): void {
    if (!view.debugPanel.open) return;
    const rects = computeDebugPanelRects(layout);
    this.drawModalFrame(rects.panel, layout.w, layout.h);
    this.drawPanelText('Debug Grants', rects.panel.x + 16, rects.panel.y + 14, layout.compact ? 19 : 22, 0xf6edd9);
    this.drawPanelText('Development shortcuts for testing resources and tech.', rects.panel.x + 16, rects.panel.y + 43, 12, 0xa9bbb3);
    view.debugPanel.options.forEach((option, index) => {
      const rect = rects.options[index];
      if (rect === undefined) return;
      this.drawDebugOption(rect, option.name, option.description, String(index + 1), debugAccent(option.command), view.debugPanel.focusedIndex === index);
    });
    this.drawPanelButton(rects.close, 'Close', true);
  }

  private drawDebugOption(rect: Rect, name: string, description: string, key: string, color: number, focused: boolean): void {
    this.ui
      .roundRect(rect.x, rect.y, rect.w, rect.h, 7)
      .fill({ color: 0x1b2822, alpha: 0.98 })
      .stroke({ color: focused ? 0xf0c866 : color, width: focused ? 2.4 : 1.7, alpha: 0.9 })
      .circle(rect.x + 22, rect.y + 24, 11)
      .fill({ color, alpha: 0.95 });
    this.drawPanelText(shortText(name, rect.w > 150 ? 15 : 12), rect.x + 40, rect.y + 12, 13, 0xf6edd9);
    this.drawPanelText(shortText(description, rect.w > 150 ? 27 : 20), rect.x + 14, rect.y + 43, 10, 0xa9bbb3);
    this.drawPanelText(key, rect.x + rect.w - 22, rect.y + 10, 12, 0xd6b963);
  }

  private drawToolRecipe(rect: Rect, name: string, description: string, status: string, owned: number, cost: string, key: string, color: number, enabled: boolean, focused: boolean): void {
    this.ui
      .roundRect(rect.x, rect.y, rect.w, rect.h, 7)
      .fill({ color: enabled ? 0x1b2822 : 0x171f1b, alpha: 0.98 })
      .stroke({ color: focused ? 0xf0c866 : enabled ? color : 0x5d6962, width: focused ? 2.4 : 1.7, alpha: 0.9 })
      .circle(rect.x + 22, rect.y + 25, 12)
      .fill({ color: enabled ? color : 0x758177, alpha: 0.95 });
    this.drawPanelText(name, rect.x + 42, rect.y + 13, 14, enabled ? 0xf6edd9 : 0x879188);
    this.drawPanelText(shortText(description, rect.w > 240 ? 40 : 34), rect.x + 14, rect.y + 43, 11, enabled ? 0xa9bbb3 : 0x758177);
    this.drawPanelText(`Cost ${cost}`, rect.x + 14, rect.y + rect.h - 42, 11, enabled ? 0xd6b963 : 0x879188);
    this.drawPanelText(`${status}${owned > 0 ? `  Held ${owned}` : ''}`, rect.x + 14, rect.y + rect.h - 22, 11, enabled ? 0x9ccf84 : 0x879188);
    this.drawPanelText(key, rect.x + rect.w - 22, rect.y + 10, 12, enabled ? 0xd6b963 : 0x879188);
  }

  private drawTechOption(rect: Rect, name: string, description: string, level: number, maxLevel: number, cost: number, key: string, color: number, enabled: boolean, focused: boolean): void {
    this.ui
      .roundRect(rect.x, rect.y, rect.w, rect.h, 7)
      .fill({ color: enabled ? 0x1b2822 : 0x171f1b, alpha: 0.98 })
      .stroke({ color: focused ? 0xf0c866 : enabled ? color : 0x5d6962, width: focused ? 2.4 : 1.7, alpha: 0.9 })
      .circle(rect.x + 20, rect.y + 22, 10)
      .fill({ color: enabled ? color : 0x758177, alpha: 0.95 });
    this.drawPanelText(name, rect.x + 38, rect.y + 12, 13, enabled ? 0xf6edd9 : 0x879188);
    this.drawPanelText(shortText(description, 34), rect.x + 14, rect.y + 34, 10, enabled ? 0xa9bbb3 : 0x758177);
    this.drawPanelText(`Lv ${level}/${maxLevel}  Cost ${cost}`, rect.x + 14, rect.y + rect.h - 20, 10, enabled ? 0xd6b963 : 0x879188);
    this.drawPanelText(key, rect.x + rect.w - 22, rect.y + 10, 12, enabled ? 0xd6b963 : 0x879188);
  }

  private drawSlot(rect: Rect, item: InventoryItem | null, focused: boolean): void {
    this.ui
      .roundRect(rect.x, rect.y, rect.w, rect.h, 6)
      .fill({ color: focused ? 0x31483f : 0x1b2822, alpha: 0.98 })
      .stroke({ color: focused ? 0xf0c866 : 0x5d6962, width: focused ? 2.2 : 1.2, alpha: 0.95 });
    if (item !== null) this.drawItemIcon(item, rect.x, rect.y, rect.w, true);
  }

  private drawItemIcon(item: InventoryItem, x: number, y: number, size: number, showLabel: boolean): void {
    const color = item.kind === 'trash' ? 0x8a6957 : item.kind === 'item' ? itemColor(item.itemId) : particleColor(item.resource ?? 'scrap');
    if (item.kind === 'trash') {
      this.ui
        .roundRect(x + size * 0.26, y + size * 0.36, size * 0.48, size * 0.32, 3)
        .fill({ color, alpha: 0.96 })
        .rect(x + size * 0.34, y + size * 0.29, size * 0.22, size * 0.18)
        .fill({ color: 0xb97d4f, alpha: 0.95 });
    } else if (item.kind === 'item' && item.itemId === 'grenade') {
      this.ui
        .circle(x + size * 0.49, y + size * 0.52, size * 0.18)
        .fill({ color, alpha: 0.96 })
        .rect(x + size * 0.45, y + size * 0.24, size * 0.15, size * 0.15)
        .fill({ color: 0xd6b963, alpha: 0.94 })
        .rect(x + size * 0.56, y + size * 0.29, size * 0.16, size * 0.04)
        .fill({ color: 0xf6edd9, alpha: 0.9 });
    } else if (item.kind === 'item' && item.itemId === 'flamethrower') {
      this.ui
        .rect(x + size * 0.25, y + size * 0.48, size * 0.38, size * 0.11)
        .fill({ color, alpha: 0.96 })
        .rect(x + size * 0.58, y + size * 0.43, size * 0.2, size * 0.08)
        .fill({ color: 0xd55c43, alpha: 0.96 })
        .rect(x + size * 0.32, y + size * 0.58, size * 0.13, size * 0.19)
        .fill({ color: 0xf6edd9, alpha: 0.78 });
    } else {
      this.ui
        .circle(x + size * 0.5, y + size * 0.45, size * 0.17)
        .fill({ color, alpha: 0.96 })
        .rect(x + size * 0.56, y + size * 0.58, size * 0.22, size * 0.1)
        .fill({ color: 0xf6edd9, alpha: 0.82 });
    }
    if (showLabel && item.quantity > 1) this.drawPanelText(String(item.quantity), x + size * 0.58, y + size * 0.58, 10, 0xf6edd9);
  }

  private drawPanelButton(rect: Rect, label: string, enabled: boolean, focused = false): void {
    this.ui
      .roundRect(rect.x, rect.y, rect.w, rect.h, 6)
      .fill({ color: enabled ? 0x27413e : 0x1a231f, alpha: 0.98 })
      .stroke({ color: focused ? 0xf0c866 : enabled ? 0xd6b963 : 0x5d6962, width: focused ? 2.2 : 1.4, alpha: 0.95 });
    this.drawPanelText(label, rect.x + 10, rect.y + rect.h * 0.28, 12, enabled ? 0xf6edd9 : 0x879188);
  }

  private drawResourcePills(view: ViewModel, layout: ScreenLayout): void {
    const compactLabels: Record<string, string> = { scrap: 'S', plastic: 'P', glass: 'G', biomass: 'B' };
    let x = 14;
    for (const id of view.resourceOrder) {
      const label = layout.compact ? compactLabels[id] : RESOURCE_LABELS[id];
      const text = `${label} ${Math.floor(view.resources[id])}`;
      const w = layout.compact ? 43 : Math.max(74, 42 + text.length * 6);
      this.ui
        .roundRect(x, 10, w, 22, 6)
        .fill({ color: 0x18231f, alpha: 0.96 })
        .stroke({ color: particleColor(id), width: 1.2, alpha: 0.72 })
        .circle(x + 11, 21, 4)
        .fill({ color: particleColor(id), alpha: 0.95 });
      this.drawPanelText(text, x + (layout.compact ? 20 : 22), 14, layout.compact ? 10 : 11, 0xf6edd9);
      x += w + 7;
    }
  }

  private drawStreak(view: ViewModel, width: number, layout: ScreenLayout): void {
    const w = layout.compact ? 170 : 240;
    const x = width * 0.5 - w * 0.5;
    const y = layout.compact ? 50 : 48;
    const progress = view.streak.value <= 0 ? 0 : Math.min(1, view.streak.timeRemainingSec / view.streak.windowSec);
    this.ui.roundRect(x, y, w, 8, 4).fill({ color: 0x1b2a24, alpha: 0.95 });
    this.ui.roundRect(x, y, w * progress, 8, 4).fill({ color: view.streak.tier >= 2 ? 0xe3c15e : 0x9ccf84, alpha: 0.95 });
    if (view.streak.value > 0) this.drawPanelText(`Combo ${Math.floor(view.streak.value)}  Tier ${view.streak.tier}`, x, y - 16, 10, 0xf6edd9);
  }

  private drawModalFrame(panel: Rect, width: number, height: number): void {
    this.ui
      .rect(0, 0, width, height)
      .fill({ color: 0x07100d, alpha: 0.38 })
      .roundRect(panel.x, panel.y, panel.w, panel.h, 8)
      .fill({ color: 0x101a16, alpha: 0.99 })
      .stroke({ color: 0xd6b963, width: 2, alpha: 0.95 });
  }

  private drawPanelText(text: string, x: number, y: number, size: number, color: number): void {
    const panelText = new Text({ text, style: { fontFamily: FONT, fontSize: size, fill: color } });
    panelText.position.set(x, y);
    this.textLayer.addChild(panelText);
  }

  private captureFx(view: ViewModel): void {
    for (const event of view.fxEvents) {
      if (this.seenFx.has(event.id)) continue;
      this.seenFx.add(event.id);
      this.particles.push({
        kind: event.kind,
        resource: event.resource,
        fromX: event.fromX,
        fromY: event.fromY,
        toX: event.toX,
        toY: event.toY,
        age: 0,
        duration: event.kind === 'dropoff' ? 0.34 : 0.28,
      });
    }
    while (this.seenFx.size > 80) {
      const first = this.seenFx.values().next().value as number | undefined;
      if (first === undefined) break;
      this.seenFx.delete(first);
    }
  }

  private drawParticles(view: ViewModel, layout: ScreenLayout): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const particle = this.particles[i];
      particle.age += 1 / 60;
      const t = Math.min(1, particle.age / particle.duration);
      const ease = 1 - (1 - t) * (1 - t);
      const wx = particle.fromX + (particle.toX - particle.fromX) * ease;
      const wy = particle.fromY + (particle.toY - particle.fromY) * ease - Math.sin(t * Math.PI) * 8;
      this.actors.circle(worldToScreenX(wx, view, layout), worldToScreenY(wy, view, layout), Math.max(3, 4 * layout.scale * (1 - t * 0.35))).fill({
        color: particleColor(particle.resource),
        alpha: 1 - t * 0.2,
      });
      if (t >= 1) this.particles.splice(i, 1);
    }
  }

  private textureForBuilding(type: BuildingType): Texture {
    if (type === 'storage') return this.textures.storage;
    if (type === 'recycler') return this.textures.recycler;
    return Texture.EMPTY;
  }
}

function worldToScreenX(worldX: number, view: ViewModel, layout: ScreenLayout): number {
  return layout.x + layout.w * 0.5 + (worldX - view.camera.x) * layout.scale;
}

function worldToScreenY(worldY: number, view: ViewModel, layout: ScreenLayout): number {
  return layout.y + layout.h * 0.5 + (worldY - view.camera.y) * layout.scale;
}

function groundColor(tile: ViewTile): number {
  if (tile.wall) return shade(tile.zoneColor, 0.62);
  if (tile.amount > 0) return shade(tile.zoneColor, 0.7);
  if (tile.restored) return shade(tile.zoneColor, 1.22);
  return [tile.zoneColor, shade(tile.zoneColor, 1.08), shade(tile.zoneColor, 0.92), shade(tile.zoneColor, 1.16)][tile.variant] ?? tile.zoneColor;
}

function shade(color: number, amount: number): number {
  const r = Math.max(0, Math.min(255, Math.round(((color >> 16) & 255) * amount)));
  const g = Math.max(0, Math.min(255, Math.round(((color >> 8) & 255) * amount)));
  const b = Math.max(0, Math.min(255, Math.round((color & 255) * amount)));
  return (r << 16) | (g << 8) | b;
}

function pileColor(amount: number): number {
  return [0x000000, 0x667065, 0x7e735f, 0x8a6957, 0x765550, 0x674d4f, 0x5f454a][amount] ?? 0x5f454a;
}

function facingX(facing: ViewModel['player']['facing']): number {
  if (facing === 'east') return 1;
  if (facing === 'west') return -1;
  return 0;
}

function facingY(facing: ViewModel['player']['facing']): number {
  if (facing === 'south') return 1;
  if (facing === 'north') return -1;
  return 0;
}

function particleColor(resource: ResourceId): number {
  if (resource === 'plastic') return 0x75aadb;
  if (resource === 'glass') return 0xc2e9e5;
  if (resource === 'biomass') return 0x9ccf84;
  return 0xb97d4f;
}

function itemColor(itemId: string): number {
  if (itemId === 'grenade') return 0x8ea06f;
  if (itemId === 'flamethrower') return 0xd55c43;
  return 0xd6b963;
}

function buildingAccent(type: BuildingType): number {
  if (type === 'storage') return 0xd6b963;
  if (type === 'gatherer') return 0x9ccf84;
  if (type === 'carrier_drone') return 0x75aadb;
  if (type === 'drill') return 0xd55c43;
  return 0x7aa590;
}

function techAccent(category: string): number {
  if (category === 'automation') return 0x75aadb;
  if (category === 'optimization') return 0xd6b963;
  return 0x9ccf84;
}

function debugAccent(command: string): number {
  if (command === 'grantTechPoint') return 0xd6b963;
  if (command === 'grantGrenade' || command === 'grantFlamethrower') return 0xd55c43;
  if (command === 'grantAllResources') return 0xc2e9e5;
  if (command === 'grantBiomass') return 0x9ccf84;
  if (command === 'grantGlass') return 0xc2e9e5;
  if (command === 'grantPlastic') return 0x75aadb;
  return 0xb97d4f;
}

function actionColor(kind: ViewModel['actionTarget']['kind']): number {
  if (kind === 'place') return 0x75aadb;
  if (kind === 'building') return 0x9ccf84;
  if (kind === 'harvest') return 0xf0c866;
  if (kind === 'blocked') return 0xd55c43;
  return 0x6d7c72;
}

function formatCostBag(cost: ViewModel['buildMenu']['options'][number]['cost']): string {
  const parts = [
    cost.scrap > 0 ? `S${cost.scrap}` : '',
    cost.plastic > 0 ? `P${cost.plastic}` : '',
    cost.glass > 0 ? `G${cost.glass}` : '',
    cost.biomass > 0 ? `B${cost.biomass}` : '',
  ].filter(Boolean);
  return parts.length === 0 ? 'free' : parts.join(' ');
}

function shortText(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(0, max - 3)).trim()}...`;
}

async function loadPixelArtTextures(): Promise<TextureSet> {
  try {
    const [recycler, storage, ...playerFlat] = await Promise.all([
      Assets.load<Texture>(new URL('../assets/sprites/png/home_recycler.png', import.meta.url).href),
      Assets.load<Texture>(new URL('../assets/sprites/png/storage_depot.png', import.meta.url).href).catch(() => Texture.EMPTY),
      ...playerFrameUrls().map((url) => Assets.load<Texture>(url)),
    ]);
    return {
      recycler,
      storage,
      player: {
        south: playerFlat.slice(0, 5),
        east: playerFlat.slice(5, 10),
        north: playerFlat.slice(10, 15),
        west: playerFlat.slice(15, 20),
      },
    };
  } catch {
    return {
      recycler: Texture.EMPTY,
      storage: Texture.EMPTY,
      player: {
        north: [Texture.EMPTY],
        east: [Texture.EMPTY],
        south: [Texture.EMPTY],
        west: [Texture.EMPTY],
      },
    };
  }
}

function playerFrameUrls(): string[] {
  const directions = ['south', 'east', 'north', 'west'] as const;
  const urls: string[] = [];
  for (const direction of directions) {
    for (let frame = 0; frame < 5; frame++) {
      urls.push(new URL(`../assets/sprites/png/player_reclaimer/${direction}_${String(frame).padStart(3, '0')}.png`, import.meta.url).href);
    }
  }
  return urls;
}
